import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    TextField,
    Button,
    CircularProgress,
    IconButton,
    Paper,
    FormControl,
    Select,
    MenuItem,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SuggestedQuestions from '../../molecules/askAsper/chat/suggestedQuestions';
import WelcomeText from '../../molecules/askAsper/chat/welcomeText';
import ChatMessages from '../../molecules/askAsper/chat/chatMessages';
import QuestionBankModal from '../../molecules/askAsper/modals/questionBankModal';
import HumanInputModal from '../../molecules/askAsper/modals/HumanInputModal';
import './chatWindow.less';
import { v4 as uuidv4 } from 'uuid';
import { getAnswers, getExcelFile, getPPTFile, getHumanQuestion, submitHumanAnswer } from '../../../api/chatAPI';
import { AskAsperLogo } from 'assets/images';

// Function to parse the API response
const parseApiResponse = (response) => {
    try {
        const data = typeof response === 'string' ? JSON.parse(response) : response;
        const content = data.content || '';
        const run_id = data.run_id || '';
        let chartData = null;
        const additional_kwargs = data.additional_kwargs || {};
        if (additional_kwargs.recharts_json) {
            try {
                chartData =
                    typeof additional_kwargs.recharts_json === 'string'
                        ? JSON.parse(additional_kwargs.recharts_json)
                        : additional_kwargs.recharts_json;
            } catch (parseError) {
                console.error('Failed to parse recharts_json:', parseError);
                chartData = null;
            }
        }

        let followupQuestions = [];
        if (additional_kwargs.followup_questions) {
            try {
                followupQuestions =
                    typeof additional_kwargs.followup_questions === 'string'
                        ? JSON.parse(additional_kwargs.followup_questions)
                        : additional_kwargs.followup_questions;
                if (!Array.isArray(followupQuestions)) {
                    followupQuestions = [];
                }
            } catch (parseError) {
                console.error('Failed to parse followup_questions:', parseError);
                followupQuestions = [];
            }
        }

        const transparency = additional_kwargs.transparency || [];
        const latest_question = additional_kwargs.latest_question || '';

        return { content, chartData, followupQuestions, additional_kwargs, transparency, latest_question, run_id };
    } catch (error) {
        console.error('Error parsing API response:', error);
        return {
            content: 'Error parsing response',
            chartData: null,
            followupQuestions: [],
            additional_kwargs: {},
            transparency: [],
            latest_question: '',
            run_id: '',
        };
    }
};

function ChatWindow({
    resetConversation,
    handleReset,
    handleEnableDisableResetBtn,
    sidebarOpen,
    conversationData,
    selectedThreadID,
    onMessagesChanged,
    startNewConversation,
    fetchChatHistory,
}) {
    const [question, setQuestion] = useState('');
    const [messages, setMessages] = useState([]);
    const [chatSessionUuid, setChatSessionUuid] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [nodeProgress, setNodeProgress] = useState([]);
    const [openQuestionBank, setOpenQuestionBank] = useState(false);
    const messagesEndRef = useRef(null);
    const chatContainerRef = useRef(null);
    const inputRef = useRef(null);
    const [selectedSource, setSelectedSource] = useState('EnterpriseData');
    const activeStreamRef = useRef(null);

    const [humanInputQuestion, setHumanInputQuestion] = useState(null);
    const [isHumanInputModalOpen, setIsHumanInputModalOpen] = useState(false);
    const pollIntervalRef = useRef(null);
    const currentThreadId = useRef(null);

    // ---- START: FIX FOR STALE STATE ----
    const isModalOpenRef = useRef(isHumanInputModalOpen);

    useEffect(() => {
        isModalOpenRef.current = isHumanInputModalOpen;
    }, [isHumanInputModalOpen]);
    // ---- END: FIX FOR STALE STATE ----

    const handleSourceChange = (event) => setSelectedSource(event.target.value);
    const handleOpenQuestionBank = () => setOpenQuestionBank(true);
    const handleCloseQuestionBank = () => setOpenQuestionBank(false);

    const stopPolling = () => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
    };

    useEffect(() => {
        const newId = selectedThreadID || chatSessionUuid;
        currentThreadId.current = newId;
    }, [selectedThreadID, chatSessionUuid]);

    useEffect(() => {
        if (conversationData && conversationData.length > 0) {
            const parsedMessages = conversationData.map((msg) => {
                const msgCopy = { ...msg };
                if (msgCopy.additional_kwargs && typeof msgCopy.additional_kwargs === 'string') {
                    try {
                        msgCopy.additional_kwargs = JSON.parse(msgCopy.additional_kwargs);
                    } catch (e) {
                        console.error(
                            'Failed to parse additional_kwargs from history string:',
                            msgCopy.additional_kwargs,
                            e,
                        );
                        msgCopy.additional_kwargs = {};
                    }
                }
                const parsedData = parseApiResponse(msgCopy);
                return { ...msg, ...parsedData, isFromHistory: true };
            });
            setMessages(parsedMessages);
        } else {
            setMessages([]);
        }
    }, [conversationData]);

    useEffect(() => {
        setChatSessionUuid(uuidv4());
        return () => stopPolling(); // Cleanup on component unmount
    }, []);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
        handleReset(false);
        handleEnableDisableResetBtn(messages.length === 0);
        onMessagesChanged(messages);
    }, [messages]);

    useEffect(() => {
        if (resetConversation || startNewConversation) {
            setChatSessionUuid(uuidv4());
            setMessages([]);
            stopPolling();
            setNodeProgress([]);
        }
    }, [resetConversation, startNewConversation]);

    const pollForHumanQuestion = () => {
        stopPolling(); // Ensure no multiple polls are running
        pollIntervalRef.current = setInterval(async () => {
            // Use the ref here to get the current value and avoid stale closures
            if (isModalOpenRef.current || !currentThreadId.current) return;

            try {
                const data = await getHumanQuestion(currentThreadId.current);
                if (data && data.question) {
                    setHumanInputQuestion(data.question);
                    setIsHumanInputModalOpen(true);
                    stopPolling();
                }
            } catch (error) {
                console.error('Error polling for human question:', error);
            }
        }, 3000);
    };

    const handleHumanInputSubmit = async (answer) => {
        setIsHumanInputModalOpen(false);
        setHumanInputQuestion(null);
        try {
            await submitHumanAnswer(currentThreadId.current, answer);
        } catch (error) {
            console.error('Failed to submit human answer, but restarting polling anyway.', error);
        } finally {
            pollForHumanQuestion();
        }
    };

    const handleSubmit = async (e, suggestedQuestion = null) => {
        e?.preventDefault();
        const currentQuestion = suggestedQuestion || question;
        if (!currentQuestion.trim()) return;

        const userMessage = { content: currentQuestion, isUser: true, run_id: uuidv4() };
        const placeholderMessage = {
            content: 'Processing...',
            isUser: false,
            run_id: `placeholder-${userMessage.run_id}`,
        };
        setMessages((prev) => [...prev, userMessage, placeholderMessage]);

        if (!suggestedQuestion) setQuestion('');

        setLoading(true);
        setError(null);
        setNodeProgress([]);

        const threadIdForRequest = selectedThreadID || chatSessionUuid;
        currentThreadId.current = threadIdForRequest;

        pollForHumanQuestion();

        try {
            const params = {
                message: currentQuestion,
                thread_id: threadIdForRequest,
                source: selectedSource,
                new_session: messages.length === 0,
            };

            const stream = getAnswers(params);
            activeStreamRef.current = stream;

            stream.onMessage((data) => {
                if (activeStreamRef.current !== stream) return;

                if (data.type === 'node_update') {
                    setNodeProgress((prev) => {
                        const existingNodeIndex = prev.findIndex((n) => n.node_name === data.node_name);
                        if (existingNodeIndex > -1) {
                            const newProgress = [...prev];
                            newProgress[existingNodeIndex] = data;
                            return newProgress;
                        }
                        return [...prev, data];
                    });
                } else if (data.type === 'ai') {
                    const parsed = parseApiResponse(data);
                    setMessages((prev) => {
                        const newMessages = [...prev];
                        let messageIndex = newMessages.findIndex((m) => m.run_id === data.run_id);
                        if (messageIndex === -1) {
                            messageIndex = newMessages.findIndex((m) => m.run_id.startsWith('placeholder-'));
                        }

                        if (messageIndex > -1) {
                            const existingMessage = newMessages[messageIndex];
                            const mergedKwargs = { ...existingMessage.additional_kwargs, ...parsed.additional_kwargs };
                            newMessages[messageIndex] = {
                                ...existingMessage,
                                ...parsed,
                                content: parsed.content,
                                additional_kwargs: mergedKwargs,
                                chartData: parsed.chartData || existingMessage.chartData,
                                run_id: data.run_id,
                                isUser: false,
                            };
                        } else {
                            newMessages.push({ ...parsed, isUser: false, run_id: data.run_id });
                        }
                        return newMessages;
                    });
                }
            });

            await stream.promise;

            try {
                const excelFileDetails = await getExcelFile({ thread_id: params.thread_id });
                if (excelFileDetails?.latest_file_url) {
                    setMessages((prev) => {
                        const newMessages = [...prev];
                        const lastMessage = newMessages[newMessages.length - 1];
                        if (lastMessage && !lastMessage.isUser) {
                            lastMessage.excelFileURL = excelFileDetails.latest_file_url;
                        }
                        return newMessages;
                    });
                }
            } catch (fileError) {
                console.error('Could not fetch Excel file, but the chat response was successful:', fileError);
            }

            try {
                fetchChatHistory();
            } catch (historyError) {
                console.error('Failed to refresh chat history:', historyError);
            }
        } catch (err) {
            setError('Failed to get answer. Please try again.');
            setMessages((prev) => {
                const filtered = prev.filter((m) => !m.run_id.startsWith('placeholder-'));
                return [
                    ...filtered,
                    { content: "Sorry, I couldn't process your request.", isUser: false, run_id: uuidv4() },
                ];
            });
        } finally {
            setLoading(false);
            activeStreamRef.current = null;
            if (inputRef.current) inputRef.current.focus();
        }
    };

    const handleSuggestedQuestionClick = (selectedQuestion) => {
        handleSubmit(null, selectedQuestion);
    };

    return (
        <Box
            sx={{
                padding: { xs: 1, sm: 2 },
                paddingRight: 0,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
            }}
        >
            <Paper
                ref={chatContainerRef}
                elevation={0}
                sx={{ backgroundColor: 'transparent', flexGrow: 1, minHeight: '12rem' }}
            >
                {messages.length === 0 && (
                    <div>
                        <img style={{ margin: '0 auto' }} src={AskAsperLogo} alt="AskAsper Logo" />
                        <WelcomeText />
                    </div>
                )}
                <ChatMessages
                    messages={messages}
                    messagesEndRef={messagesEndRef}
                    onQuestionClick={handleSuggestedQuestionClick}
                    chatSessionUuid={chatSessionUuid}
                    nodeProgress={nodeProgress}
                    isStreaming={loading}
                />
            </Paper>
            <Box>
                {messages.length === 0 && <SuggestedQuestions onQuestionClick={handleSuggestedQuestionClick} />}
                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    className={`input-section ${sidebarOpen ? 'sidebar-open' : ''}`}
                >
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Ask Anything..."
                        inputRef={inputRef}
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        disabled={loading}
                        className="input-field"
                        multiline
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSubmit(e);
                            }
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 16,
                            left: 16,
                            right: 16,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FormControl size="small">
                                <Select
                                    value={selectedSource}
                                    onChange={handleSourceChange}
                                    sx={{ fontSize: '12px', borderRadius: '99px', width: '150px' }}
                                >
                                    <MenuItem value="EnterpriseData">Enterprise Data</MenuItem>
                                    <MenuItem value="Learning">Learning</MenuItem>
                                    <MenuItem value="Action">Action</MenuItem>
                                </Select>
                            </FormControl>
                            {/* <Button
                                startIcon={<AttachFileIcon />}
                                variant="outlined"
                                className="attach-button"
                                size="small"
                            >
                                Attach
                            </Button> */}
                            <Button
                                startIcon={<MenuBookIcon />}
                                variant="outlined"
                                className="question-bank-btn"
                                size="small"
                                onClick={handleOpenQuestionBank}
                            >
                                Question Bank
                            </Button>
                        </Box>
                        <IconButton className="send-button" type="submit" disabled={loading}>
                            {loading ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : (
                                <ArrowForwardIcon color="#44338A" />
                            )}
                        </IconButton>
                    </Box>
                </Box>
            </Box>

            <QuestionBankModal
                open={openQuestionBank}
                onClose={handleCloseQuestionBank}
                onQuestionSelect={handleSuggestedQuestionClick}
            />
            <HumanInputModal
                open={isHumanInputModalOpen}
                question={humanInputQuestion}
                onSubmit={handleHumanInputSubmit}
            />
        </Box>
    );
}

export default ChatWindow;

// import React, { useState, useEffect, useRef } from 'react';
// import {
//     Box,
//     TextField,
//     Button,
//     CircularProgress,
//     IconButton,
//     Paper,
//     FormControl,
//     Select,
//     MenuItem
// } from '@mui/material';
// import AttachFileIcon from '@mui/icons-material/AttachFile';
// import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
// import MenuBookIcon from '@mui/icons-material/MenuBook';
// import SuggestedQuestions from '../../components/chat/suggestedQuestions';
// import WelcomeText from '../../components/chat/welcomeText';
// import ChatMessages from '../../components/chat/chatMessages';
// import QuestionBankModal from '../../components/modals/questionBankModal';
// import './chatWindow.less';
// import { v4 as uuidv4 } from 'uuid';
// import { getAnswers, getExcelFile, getPPTFile } from '../../api/chatAPI';

// // Function to parse the API response
// const parseApiResponse = (response) => {
//     try {
//         const data = typeof response === 'string' ? JSON.parse(response) : response;
//         console.log("data", data)
//         const content = data.content || '';
//         let run_id = data.run_id || '';
//         let chartData = null;
//         let additional_kwargs = data.additional_kwargs || {};
//         if (additional_kwargs.recharts_json) {
//             if (typeof additional_kwargs.recharts_json === 'string') {
//                 try {
//                     chartData = JSON.parse(additional_kwargs.recharts_json);
//                     console.log('parseApiResponse: Parsed recharts_json:', chartData);
//                 } catch (parseError) {
//                     console.error('parseApiResponse: Failed to parse recharts_json string:', parseError);
//                     chartData = null;
//                 }
//             } else if (typeof additional_kwargs.recharts_json === 'object') {
//                 chartData = additional_kwargs.recharts_json;
//                 console.log('parseApiResponse: Using pre-parsed recharts_json:', chartData);
//             }
//         }
//         let followupQuestions = [];
//         if (additional_kwargs.followup_questions) {
//             try {
//                 followupQuestions = typeof additional_kwargs.followup_questions === 'string'
//                     ? JSON.parse(additional_kwargs.followup_questions)
//                     : additional_kwargs.followup_questions;
//                 console.log('parseApiResponse: Parsed followup_questions:', followupQuestions);
//             } catch (parseError) {
//                 console.error('parseApiResponse: Failed to parse followup_questions:', parseError);
//             }
//         }
//         let transparency = [];
//         if (additional_kwargs.transparency) {
//             try {
//                 transparency = typeof additional_kwargs.transparency === 'string'
//                     ? JSON.parse(additional_kwargs.transparency)
//                     : additional_kwargs.transparency;
//                 console.log('parseApiResponse: Parsed transparency:', transparency);
//             } catch (parseError) {
//                 console.error('parseApiResponse: Failed to parse transparency:', parseError);
//             }
//         }
//         let latest_question = '';
//         if (additional_kwargs.latest_question) {
//             latest_question = additional_kwargs.latest_question;
//             console.log('parseApiResponse: latest_question:', latest_question);
//         }
//         return { content, chartData, followupQuestions, additional_kwargs, transparency, latest_question, run_id };
//     } catch (error) {
//         console.error('parseApiResponse: Error parsing API response:', error);
//         return { content: 'Error parsing response', chartData: null, followupQuestions: [], additional_kwargs: {}, transparency: [], latest_question: '' };
//     }
// };

// function ChatWindow({
//     resetConversation,
//     handleReset,
//     handleEnableDisableResetBtn,
//     sidebarOpen,
//     conversationData,
//     selectedThreadID,
//     onMessagesChanged,
//     startNewConversation,
//     fetchChatHistory
// }) {
//     const [question, setQuestion] = useState('');
//     const [messages, setMessages] = useState([]);
//     const [chatSessionUuid, setChatSessionUuid] = useState('');
//     const [loading, setLoading] = useState(false);
//     const [error, setError] = useState(null);
//     const [nodeProgress, setNodeProgress] = useState([]);
//     const [openQuestionBank, setOpenQuestionBank] = useState(false);
//     const messagesEndRef = useRef(null);
//     const chatContainerRef = useRef(null);
//     const inputRef = useRef(null);
//     const [selectedSource, setSelectedSource] = useState('EnterpriseData');
//     const activeStreamRef = useRef(null);
//     const isUserScrolling = useRef(false);
//     const isStreaming = useRef(false);

//     const handleSourceChange = (event) => {
//         setSelectedSource(event.target.value);
//         console.log('handleSourceChange: Selected source:', event.target.value);
//     };

//     const handleOpenQuestionBank = () => {
//         console.log('handleOpenQuestionBank: Opening question bank modal');
//         setOpenQuestionBank(true);
//     };

//     const handleCloseQuestionBank = () => {
//         console.log('handleCloseQuestionBank: Closing question bank modal');
//         setOpenQuestionBank(false);
//     };

//     // Load conversation data
//     useEffect(() => {
//         console.log('useEffect[conversationData]: Loading conversation data, length:', conversationData.length);
//         if (conversationData.length > 0) {
//             const parsedMessages = conversationData.map(msg => {
//                 const parsed = parseApiResponse(msg);
//                 return {
//                     ...msg,
//                     content: parsed.content,
//                     chartData: parsed.chartData,
//                     followupQuestions: parsed.followupQuestions,
//                     additional_kwargs: parsed.additional_kwargs,
//                     transparency: parsed.transparency,
//                     latest_question: parsed.latest_question,
//                     isFromHistory: true
//                 };
//             });
//             setMessages(parsedMessages);
//             console.log('useEffect[conversationData]: Parsed messages:', parsedMessages);
//         } else {
//             setMessages([]);
//         }
//     }, [conversationData]);

//     // Initialize chat session UUID
//     useEffect(() => {
//         const newChatSessionUuid = uuidv4();
//         console.log('useEffect[]: Initializing new chat session UUID:', newChatSessionUuid);
//         setChatSessionUuid(newChatSessionUuid);
//     }, []);

//     const isAtBottom = () => {
//         if (!chatContainerRef.current) return true;
//         const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
//         const atBottom = scrollTop + clientHeight >= scrollHeight - 10;
//         console.log('isAtBottom:', atBottom, { scrollTop, scrollHeight, clientHeight });
//         return atBottom;
//     };

//     const smoothScrollToBottom = () => {
//         if (!chatContainerRef.current || !isAtBottom() || isUserScrolling.current || isStreaming.current) {
//             console.log('smoothScrollToBottom: Skipped', {
//                 isAtBottom: isAtBottom(),
//                 isUserScrolling: isUserScrolling.current,
//                 isStreaming: isStreaming.current
//             });
//             return;
//         }
//         console.log('smoothScrollToBottom: Executing scroll');
//         const startTime = performance.now();
//         const startScroll = chatContainerRef.current.scrollTop;
//         const targetScroll = chatContainerRef.current.scrollHeight - chatContainerRef.current.clientHeight;
//         const distance = targetScroll - startScroll;
//         const duration = 500;

//         const animateScroll = (currentTime) => {
//             const elapsed = currentTime - startTime;
//             const progress = Math.min(elapsed / duration, 1);
//             const ease = progress * (2 - progress);
//             chatContainerRef.current.scrollTop = startScroll + distance * ease;
//             if (progress < 1) {
//                 requestAnimationFrame(animateScroll);
//             }
//         };
//         requestAnimationFrame(animateScroll);
//     };

//     // Force scroll to bottom for user messages

//     const scrollToBottom = () => {
//         messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
//     };
//     useEffect(() => {
//         const handleScroll = () => {
//             isUserScrolling.current = !isAtBottom();
//             console.log('handleScroll: isUserScrolling set to', isUserScrolling.current);
//         };
//         const container = chatContainerRef.current;
//         if (container) {
//             container.addEventListener('scroll', handleScroll);
//         }
//         return () => {
//             if (container) {
//                 container.removeEventListener('scroll', handleScroll);
//             }
//         };
//     }, []);

//     useEffect(() => {
//         console.log('useEffect[messages]: Messages updated, length:', messages.length, 'isStreaming:', isStreaming.current);

//         // Check if the last message is from user and force scroll to bottom
//         if (messages.length > 0 && messages[messages.length - 1].isUser) {
//             console.log('useEffect[messages]: User message detected, forcing scroll to bottom');
//             // Use requestAnimationFrame to ensure DOM is fully rendered
//             requestAnimationFrame(() => {
//                 setTimeout(() => scrollToBottom(), 0);
//             });
//         } else if (!isStreaming.current) {
//             smoothScrollToBottom();
//         }

//         handleReset(false);
//         if (messages.length) {
//             handleEnableDisableResetBtn(false);
//         } else {
//             handleEnableDisableResetBtn(true);
//         }
//         onMessagesChanged(messages);
//     }, [messages]);

//     useEffect(() => {
//         if (resetConversation || startNewConversation) {
//             const newChatSessionUuid = uuidv4();
//             console.log('useEffect[resetConversation, startNewConversation]: Resetting with new UUID:', newChatSessionUuid);
//             setChatSessionUuid(newChatSessionUuid);
//             setMessages([]);
//             smoothScrollToBottom();
//         }
//     }, [resetConversation, startNewConversation]);

//     const handleSubmit = async (e, suggestedQuestion = null) => {
//         e?.preventDefault();
//         const currentQuestion = suggestedQuestion || question;
//         if (!currentQuestion.trim()) {
//             console.log('handleSubmit: Empty question, skipping');
//             return;
//         }

//         if (inputRef.current) {
//             inputRef.current.focus();
//         }

//         const userMessage = {
//             content: currentQuestion,
//             isUser: true,
//             chartData: null,
//             followupQuestions: [],
//             excelFileURL: '',
//             pptFileURL: '',
//             additional_kwargs: {},
//             transparency: '',
//             latest_question: '',
//             isFromHistory: false
//         };

//         console.log('handleSubmit: Adding user message:', currentQuestion);
//         setMessages(prev => {
//             if (prev.length && prev[prev.length - 1].isUser && prev[prev.length - 1].content === currentQuestion) {
//                 console.log('handleSubmit: Duplicate user message detected, skipping');
//                 return prev;
//             }
//             const newMessages = [...prev, userMessage, { ...userMessage, content: 'Processing...', isUser: false, isFromHistory: false }];
//             console.log('handleSubmit: Messages updated, scrolling to bottom');
//             // Force scroll to bottom immediately after user message is added
//             setTimeout(() => scrollToBottom(), 0);
//             return newMessages;
//         });

//         if (!suggestedQuestion) {
//             setQuestion('');
//         }

//         setLoading(true);
//         setError(null);
//         setNodeProgress([]);
//         isStreaming.current = true;

//         try {
//             let params = {
//                 message: currentQuestion,
//                 thread_id: conversationData.length > 0 ? selectedThreadID : chatSessionUuid,
//                 source: selectedSource,
//                 new_session: messages.length === 0,
//             };
//             console.log('handleSubmit: Starting stream with params:', params);

//             const stream = getAnswers(params);
//             console.log('handleSubmit: Stream object created:', stream);
//             activeStreamRef.current = stream;

//             stream.onMessage((data) => {
//                 if (activeStreamRef.current !== stream) {
//                     console.log('handleSubmit: Ignoring update from stale stream');
//                     return;
//                 }

//                 console.log('handleSubmit: Received stream update:', data);

//                 if (data.type === 'node_update') {
//                     console.log('handleSubmit: Node update:', data.node_name, data.status);
//                     setNodeProgress((prev) => {
//                         const existingNode = prev.find((n) => n.node_name === data.node_name);
//                         if (existingNode) {
//                             return prev.map((n) =>
//                                 n.node_name === data.node_name ? { ...n, status: data.status } : n
//                             );
//                         }
//                         return [...prev, { node_name: data.node_name, status: data.status }];
//                     });
//                 } else if (data.type === 'ai') {
//                     console.log('handleSubmit: AI message received:', data.content);
//                     const { content, chartData, followupQuestions, additional_kwargs, transparency, latest_question, run_id } = parseApiResponse(data);
//                     setMessages(prev => {
//                         const newMessages = [...prev];
//                         if (newMessages[newMessages.length - 1].isUser) {
//                             console.log('handleSubmit: Appending new AI message');
//                             newMessages.push({
//                                 content,
//                                 isUser: false,
//                                 chartData,
//                                 followupQuestions,
//                                 additional_kwargs,
//                                 transparency,
//                                 latest_question,
//                                 excelFileURL: '',
//                                 pptFileURL: '',
//                                 isFromHistory: false,
//                             });
//                         } else {
//                             console.log('handleSubmit: Updating last AI message');
//                             newMessages[newMessages.length - 1] = {
//                                 content,
//                                 isUser: false,
//                                 chartData,
//                                 followupQuestions,
//                                 additional_kwargs,
//                                 transparency,
//                                 latest_question,
//                                 excelFileURL: newMessages[newMessages.length - 1].excelFileURL,
//                                 pptFileURL: newMessages[newMessages.length - 1].pptFileURL,
//                                 isFromHistory: false,
//                                 run_id
//                             };
//                         }
//                         return newMessages;
//                     });
//                 }
//             });

//             await stream.promise;
//             console.log('handleSubmit: Stream completed, fetching Excel file and chat history');

//             const excelFileDetails = await getExcelFile({
//                 thread_id: conversationData.length > 0 ? selectedThreadID : chatSessionUuid
//             });
//             console.log('handleSubmit: getExcelFile response:', excelFileDetails);

//             setMessages(prev => {
//                 const newMessages = [...prev];
//                 const lastMessageIndex = newMessages.length - 1;
//                 if (lastMessageIndex < 0 || newMessages[lastMessageIndex].isUser) {
//                     console.warn('handleSubmit: No valid AI message to update with Excel URL, appending error message');
//                     newMessages.push({
//                         content: 'Error: No AI message to attach Excel file',
//                         isUser: false,
//                         chartData: null,
//                         followupQuestions: [],
//                         excelFileURL: excelFileDetails.latest_file_url || '',
//                         pptFileURL: '',
//                         additional_kwargs: {},
//                         transparency: [],
//                         latest_question: '',
//                         isFromHistory: false
//                     });
//                 } else {
//                     console.log('handleSubmit: Updating last AI message with excelFileURL:', excelFileDetails.latest_file_url || '');
//                     newMessages[lastMessageIndex] = {
//                         ...newMessages[lastMessageIndex],
//                         excelFileURL: excelFileDetails.latest_file_url || ''
//                     };
//                 }
//                 console.log('handleSubmit: Updated messages with excelFileURL:', newMessages[lastMessageIndex]);
//                 return newMessages;
//             });

//             fetchChatHistory();
//         } catch (error) {
//             console.error('handleSubmit: Error:', error);
//             setError('Failed to get answer from the API. Please try again.');
//             setMessages(prev => {
//                 const newMessages = [...prev];
//                 if (newMessages[newMessages.length - 1].isUser) {
//                     newMessages.push({
//                         content: "Sorry, I couldn't process your request. Please try again.",
//                         isUser: false,
//                         chartData: null,
//                         followupQuestions: [],
//                         excelFileURL: '',
//                         pptFileURL: '',
//                         additional_kwargs: {},
//                         transparency: '',
//                         latest_question: '',
//                         isFromHistory: false
//                     });
//                 } else {
//                     newMessages[newMessages.length - 1] = {
//                         content: "Sorry, I couldn't process your request. Please try again.",
//                         isUser: false,
//                         chartData: null,
//                         followupQuestions: [],
//                         excelFileURL: newMessages[newMessages.length - 1].excelFileURL,
//                         pptFileURL: newMessages[newMessages.length - 1].pptFileURL,
//                         additional_kwargs: {},
//                         transparency: [],
//                         latest_question: '',
//                         isFromHistory: false
//                     };
//                 }
//                 return newMessages;
//             });
//         } finally {
//             console.log('handleSubmit: Stream finished, scrolling to bottom');
//             setLoading(false);
//             activeStreamRef.current = null;
//             isStreaming.current = false;
//             smoothScrollToBottom();
//             setTimeout(() => {
//                 if (inputRef.current) {
//                     inputRef.current.focus();
//                 }
//             }, 10);
//         }
//     };

//     const handleSuggestedQuestionClick = (selectedQuestion) => {
//         console.log('handleSuggestedQuestionClick: Selected question:', selectedQuestion);
//         setMessages(prev => {
//             const newMessages = [...prev, {
//                 content: selectedQuestion,
//                 isUser: true,
//                 chartData: null,
//                 followupQuestions: [],
//                 excelFileURL: '',
//                 pptFileURL: '',
//                 additional_kwargs: {},
//                 transparency: '',
//                 isFromHistory: false
//             }];
//             console.log('handleSuggestedQuestionClick: Messages updated');
//             return newMessages;
//         });
//         handleSubmit(null, selectedQuestion);
//     };

//     return (
//         <Box
//             sx={{
//                 padding: { xs: 1, sm: 2 },
//                 overflowY: 'auto',
//                 display: 'flex',
//                 flexDirection: 'column',
//                 flexGrow: 1,
//             }}
//         >
//             <Paper
//                 ref={chatContainerRef}
//                 elevation={1}
//                 sx={{
//                     backgroundColor: 'transparent',
//                     boxShadow: 'none',
//                     flexGrow: 1,
//                     minHeight: '12rem',
//                 }}
//             >
//                 {messages.length === 0 && <WelcomeText />}
//                 {messages.length > 0 && (
//                     <ChatMessages
//                         messages={messages}
//                         messagesEndRef={messagesEndRef}
//                         onQuestionClick={handleSuggestedQuestionClick}
//                         chatSessionUuid={chatSessionUuid}
//                         nodeProgress={nodeProgress}
//                         isStreaming={isStreaming.current}
//                     />
//                 )}
//             </Paper>
//             <div elevation={1}>
//                 {messages.length === 0 && <SuggestedQuestions onQuestionClick={handleSuggestedQuestionClick} />}
//                 <Box component="form" onSubmit={handleSubmit} className={`input-section ${sidebarOpen ? 'sidebar-open abc' : 'abc'}`}>
//                     <TextField
//                         fullWidth
//                         variant="outlined"
//                         placeholder="Ask Cali Q..."
//                         inputRef={inputRef}
//                         value={question}
//                         onChange={(e) => setQuestion(e.target.value)}
//                         disabled={loading}
//                         className="input-field"
//                         multiline
//                         onKeyDown={(e) => {
//                             if (e.key === 'Enter' && !e.shiftKey) {
//                                 e.preventDefault();
//                                 console.log('TextField: Enter key pressed, submitting');
//                                 handleSubmit(e);
//                             }
//                         }}
//                         sx={{
//                             wordBreak: 'break-word',
//                             overflowWrap: 'break-word',
//                             maxHeight: '60px',
//                             overflowY: 'auto'
//                         }}
//                     />
//                     <Box
//                         sx={{
//                             position: 'absolute',
//                             bottom: 16,
//                             left: 16,
//                             right: 16,
//                             display: 'flex',
//                             justifyContent: 'space-between',
//                             alignItems: 'center'
//                         }}
//                     >
//                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                             <FormControl size="small">
//                                 <Select
//                                     labelId="source-select-label"
//                                     id="source-select"
//                                     value={selectedSource}
//                                     onChange={handleSourceChange}
//                                     sx={{
//                                         fontSize: '12px',
//                                         color: '#022A3E',
//                                         borderRadius: '99px',
//                                         width: '150px',
//                                     }}
//                                 >
//                                     <MenuItem value="EnterpriseData" sx={{ fontSize: '12px', color: '#022A3E' }}>
//                                         Enterprise Data
//                                     </MenuItem>
//                                     <MenuItem value="Learning" sx={{ fontSize: '12px', color: '#022A3E' }}>
//                                         Learning
//                                     </MenuItem>
//                                     <MenuItem value="Action" sx={{ fontSize: '12px', color: '#022A3E' }}>
//                                         Action
//                                     </MenuItem>
//                                 </Select>
//                             </FormControl>
//                             <Button
//                                 startIcon={<AttachFileIcon />}
//                                 variant="outlined"
//                                 className="attach-button"
//                                 size="small"
//                             >
//                                 Attach
//                             </Button>
//                             <Button
//                                 startIcon={<MenuBookIcon />}
//                                 variant="outlined"
//                                 className="question-bank-btn"
//                                 size="small"
//                                 onClick={handleOpenQuestionBank}
//                             >
//                                 Question Bank
//                             </Button>
//                         </Box>
//                         <IconButton className="send-button" type="submit">
//                             {loading ? <CircularProgress size={20} color="inherit" /> : <ArrowForwardIcon />}
//                         </IconButton>
//                     </Box>
//                 </Box>
//             </div>

//             <QuestionBankModal
//                 open={openQuestionBank}
//                 onClose={handleCloseQuestionBank}
//                 onQuestionSelect={handleSuggestedQuestionClick}
//             />
//         </Box>
//     );
// }

// export default ChatWindow;
